/**
 * A plan approval wears the PLAN face, not the budget-change face
 * (spec #44 §1.2 (c)).
 *
 * It is the same `action_proposals` record and the same approve/decline
 * endpoints as any other proposal — what differs is what a reader needs to
 * see. `proposal_metadata.plan_version` is the whole signal: a proposal
 * that carries one is a plan version waiting on its owner, and rendering
 * it as "reduce budget on campaign X" would describe the mechanism instead
 * of the decision.
 */

import { Badge, BlockStack, Button, Card, InlineStack, Text } from '@shopify/polaris';

import type { WorkspaceQuestion } from '../../types/workspace';
import { QuestionOwner } from './QuestionCard';
import styles from './workspace.module.css';

export interface PlanApprovalCardProps {
  question: WorkspaceQuestion;
  agentNames: Record<string, string>;
  onApprove: (question: WorkspaceQuestion) => void;
  onDecline: (question: WorkspaceQuestion, reason?: string) => void;
  busy?: boolean;
}

export function PlanApprovalCard({
  question,
  agentNames,
  onApprove,
  onDecline,
  busy = false,
}: PlanApprovalCardProps) {
  const proposal = question.proposal;
  if (!proposal) return null;

  return (
    <div className={styles.questionCard} data-testid="plan-approval-card">
      <Card>
        <BlockStack gap="200">
          <InlineStack gap="200" blockAlign="center">
            <Badge tone="attention">Plan approval</Badge>
            <span className={styles.mono}>Plan v{proposal.plan_version}</span>
          </InlineStack>
          <QuestionOwner question={question} agentNames={agentNames} />
          <Text as="p">{proposal.expected_effect}</Text>
          <Text as="p" tone="subdued">
            {proposal.risk_disclaimer}
          </Text>
          <InlineStack gap="200">
            <Button
              variant="primary"
              loading={busy}
              onClick={() => onApprove(question)}
            >
              Approve plan
            </Button>
            <Button loading={busy} onClick={() => onDecline(question)}>
              Decline
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>
    </div>
  );
}
